// biome-ignore-all lint/correctness/noUnusedFunctionParameters: TODO - address later
import { differenceInCalendarDays, isValid, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";

export type ChartGrouping = "day" | "week" | "month" | "quarter" | "year";

/**
 * Subjective defaults for how we bucket timelines when a user first flips to the chart.
 * These spans were picked by design because they tend to keep the chart legible without
 * immediate tweaking (roughly 3 months, 2 years, 5 years, 10 years).
 */
const AUTO_INTERVAL_THRESHOLDS = {
  DAY_MAX: 90, // ~3 months
  WEEK_MAX: 720, // ~2 years
  MONTH_MAX: 1825, // ~5 years
  QUARTER_MAX: 3650, // ~10 years
} as const;

interface UseAutoChartIntervalParams {
  dateColumns: Array<{ key: string }>;
  dataRows: Record<string, unknown>[];
  metricKey?: string | number | null;
  initialGrouping?: ChartGrouping;
}

interface UseAutoChartIntervalResult {
  grouping: ChartGrouping;
  handleGroupingChange: (next: ChartGrouping) => void;
  autoGrouping: ChartGrouping | null;
}

export function useAutoChartInterval({
  dateColumns,
  dataRows,
  initialGrouping = "day",
}: UseAutoChartIntervalParams): UseAutoChartIntervalResult {
  const [grouping, setGrouping] = useState<ChartGrouping>(initialGrouping);
  const [userOverride, setUserOverride] = useState(false);

  const autoGrouping = useMemo<ChartGrouping | null>(() => {
    if (!dateColumns.length || !dataRows.length) {
      return null;
    }

    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    dataRows.forEach((row) => {
      dateColumns.forEach((col) => {
        const raw = row[col.key];
        if (typeof raw === "string" && raw) {
          const parsed = parseISO(raw);
          if (isValid(parsed)) {
            if (!minDate || parsed < minDate) {
              minDate = parsed;
            }
            if (!maxDate || parsed > maxDate) {
              maxDate = parsed;
            }
          }
        }
      });
    });

    if (!minDate || !maxDate) {
      return null;
    }

    const spanDays = Math.max(0, differenceInCalendarDays(maxDate, minDate));

    if (spanDays < AUTO_INTERVAL_THRESHOLDS.DAY_MAX) return "day";
    if (spanDays <= AUTO_INTERVAL_THRESHOLDS.WEEK_MAX) return "week";
    if (spanDays <= AUTO_INTERVAL_THRESHOLDS.MONTH_MAX) return "month";
    if (spanDays <= AUTO_INTERVAL_THRESHOLDS.QUARTER_MAX) return "quarter";
    return "year";
  }, [dateColumns, dataRows]);

  useEffect(() => {
    setUserOverride(false);
  }, []);

  useEffect(() => {
    if (!autoGrouping) {
      return;
    }
    if (!userOverride && grouping !== autoGrouping) {
      setGrouping(autoGrouping);
    }
  }, [autoGrouping, grouping, userOverride]);

  const handleGroupingChange = (next: ChartGrouping) => {
    setGrouping(next);
    setUserOverride(true);
  };

  return { grouping, handleGroupingChange, autoGrouping };
}
